<template>
    <ContentPanel :headline="t('generators.portfolioOverview.flaggedGenerators.headline')">
        <div class="flagged-generators">
            <FlaggedGeneratorsSelector
                v-model:issue-type="selectedIssue"
                v-model:group-sid="selectedGroup"
                v-model:plant-sids="selectedPlants"
            />
            <QueryLoaderGuard
                :is-pending="isFlaggedPending"
                :is-error="isFlaggedError"
            >
                <div
                    v-if="data?.items && data.items?.length > 0"
                    class="flagged-generators__generators"
                >
                    <div class="flagged-generators__generators__list">
                        <FlaggedGeneratorItem
                            v-for="(item, index) in flaggedGeneratorsList"
                            :key="index"
                            v-tooltip="item.generatorName"
                            :generator="getShortGeneratorName(item)"
                        />
                    </div>
                    <pbl-button
                        v-if="showToggleButton"
                        compact
                        class="flagged-generators__generators__show-all-button"
                        @click="toggleShowAll"
                    >
                        {{
                            isShowAll
                                ? t('generators.portfolioOverview.flaggedGenerators.toggle.showLess')
                                : t('generators.portfolioOverview.flaggedGenerators.toggle.showMore', {
                                      count: labelCount,
                                  })
                        }}
                    </pbl-button>
                </div>
                <EmptyStatePanelContent v-else />
            </QueryLoaderGuard>
        </div>
    </ContentPanel>
</template>

<script setup lang="ts">
import { usePortfolioFlaggedGenerators } from '@/api/queries/generators/portfolio-overview/usePortfolioFlaggedGenerators'
import { computed, ref, watch } from 'vue'
import EmptyStatePanelContent from '@/components/states/EmptyStatePanelContent.vue'
import { GeneratorIssueTypes } from '@/configuration/issues/issue-types'
import { useI18n } from 'vue-i18n'
import QueryLoaderGuard from '@/components/guards/QueryLoaderGuard.vue'
import FlaggedGeneratorItem from '@/pages/generators/portfolio-overview/flagged-generators/FlaggedGeneratorItem.vue'
import FlaggedGeneratorsSelector from '@/pages/generators/portfolio-overview/flagged-generators/FlaggedGeneratorsSelector.vue'
import ContentPanel from '@/components/panel/ContentPanel.vue'
import {
    flattenGeneratorsHierarchy,
    useGeneratorsHierarchyQuery,
} from '@/api/queries/generators/useGeneratorsHierarchyQuery'
import { dedupeArray } from '@/utils/array-utils'
import { FLAGGED_GENERATORS_KEY } from '@/utils/flagged-generators-utils'
import { FlaggedGeneratorDto } from '@/api/generated/HCA'

const ALL_ITEMS_VALUE = -1
const DEFAULT_MAX_SHOWN_ITEMS = 5
const GENERATOR_NAME_MAX_LENGTH = 25

const isShowAll = ref(false)

const { t } = useI18n()

const { data: hierarchyData } = useGeneratorsHierarchyQuery()
const flattenedHierarchy = computed(() => flattenGeneratorsHierarchy(hierarchyData.value?.plantGroups ?? []))

const storedParamsRaw = localStorage.getItem(FLAGGED_GENERATORS_KEY)
const storedParams = storedParamsRaw && JSON.parse(storedParamsRaw)

// Fixed issue type: Condition status changed in the last 30 days
const selectedIssue = ref<number | undefined>(storedParams?.issueType ?? GeneratorIssueTypes[0].value)
const selectedGroup = ref<number | undefined>(storedParams?.groupSid ?? ALL_ITEMS_VALUE)
const selectedPlants = ref<number[]>(storedParams?.plantSids ?? [ALL_ITEMS_VALUE])

const plantSids = computed(() => {
    if (!selectedPlants.value.includes(ALL_ITEMS_VALUE)) {
        return selectedPlants.value
    }

    const applicablePlantSids: number[] = []

    if (selectedGroup.value === ALL_ITEMS_VALUE) {
        applicablePlantSids.push(...flattenedHierarchy.value.map((e) => e.plant.sid))
    } else {
        applicablePlantSids.push(
            ...flattenedHierarchy.value.filter((e) => e.plantGroup.sid === selectedGroup.value).map((e) => e.plant.sid),
        )
    }

    return dedupeArray(applicablePlantSids)
})

const {
    data,
    isPending: isFlaggedPending,
    isError: isFlaggedError,
} = usePortfolioFlaggedGenerators(selectedIssue, plantSids)

watch(
    [selectedIssue, selectedGroup, selectedPlants],
    ([newSelectedIssue, newSelectedGroup, newSelectedPlants]) => {
        const storedPayload = {
            issueType: newSelectedIssue,
            groupSid: newSelectedGroup,
            plantSids: newSelectedPlants,
        }

        localStorage.setItem(FLAGGED_GENERATORS_KEY, JSON.stringify(storedPayload))
    },
    { immediate: true },
)

const labelCount = computed(() => {
    if (!data.value?.totalCount || !showToggleButton.value) {
        return
    }

    return data.value.totalCount - DEFAULT_MAX_SHOWN_ITEMS
})

const showToggleButton = computed(() => {
    return data.value?.totalCount && data.value.totalCount > DEFAULT_MAX_SHOWN_ITEMS
})

const flaggedGeneratorsList = computed(() => {
    if (!data.value?.items) {
        return []
    }

    return isShowAll.value ? data.value.items : data.value.items.slice(0, DEFAULT_MAX_SHOWN_ITEMS)
})

const toggleShowAll = () => {
    isShowAll.value = !isShowAll.value
}

const getShortGeneratorName = (item: FlaggedGeneratorDto) => {
    const name = item.generatorName ?? ''
    const shortName =
        name.length > GENERATOR_NAME_MAX_LENGTH ? name.substring(0, GENERATOR_NAME_MAX_LENGTH) + '...' : name

    return {
        ...item,
        generatorName: shortName,
    }
}
</script>

<style scoped lang="scss">
.flagged-generators {
    display: flex;
    flex-direction: column;
    gap: $spacing-s;

    &__generators {
        display: flex;
        flex-direction: column;

        &__list {
            display: flex;
            flex-wrap: wrap;
            gap: $spacing-xs;
        }

        &__show-all-button {
            width: max-content;
            padding-top: $spacing-s;
        }
    }
}
</style>
